import { z } from 'zod';

import type {
  AlphanumericConfig,
  EntityConfig,
  NumberConfig,
  PhoneNumberConfig,
  TimeConfig,
} from '@polyai/schemas';
import { EntityType } from '@polyai/schemas';
import { AlphanumericValidationType } from '@polyai/stream/store/reducers/entities/types';

export const getDefaultEntityConfig = (
  type: EntityType,
): EntityConfig | undefined => {
  switch (type) {
    case EntityType.NUMERIC:
      return {
        $case: EntityType.NUMERIC,
        value: { hasDecimal: false, hasRange: false },
      };
    case EntityType.ALPHANUMERIC:
      return {
        $case: EntityType.ALPHANUMERIC,
        value: { enabled: false, validationType: '', regularExpression: '' },
      };
    case EntityType.ENUM:
      return {
        $case: EntityType.ENUM,
        value: { options: [''] },
      };
    case EntityType.DATE:
      return {
        $case: EntityType.DATE,
        value: { relativeDate: false },
      };
    case EntityType.TIME:
      return {
        $case: EntityType.TIME,
        value: { enabled: false, startTime: '', endTime: '' },
      };
    case EntityType.PHONE_NUMBER:
      return {
        $case: EntityType.PHONE_NUMBER,
        value: { countryCodes: [], enabled: false },
      };
    default:
      return undefined;
  }
};

export const validateEntityConfig = (
  config: EntityConfig,
  ctx: z.RefinementCtx,
) => {
  switch (config.$case) {
    case EntityType.NUMERIC:
      validateNumericConfig(config, ctx);
      break;

    case EntityType.ALPHANUMERIC:
      validateAlphanumericConfig(config, ctx);
      break;

    case EntityType.PHONE_NUMBER:
      validatePhoneNumberConfig(config, ctx);
      break;

    case EntityType.TIME:
      validateTimeConfig(config, ctx);
      break;

    default:
      break;
  }
};

const validateNumericConfig = (config: NumberConfig, ctx: z.RefinementCtx) => {
  const limitsEnabled = config?.value?.hasRange;
  if (
    limitsEnabled &&
    config.value.max &&
    config.value.min &&
    config.value.max < config.value.min
  ) {
    ctx.addIssue({
      path: ['config', 'value', 'max'],
      code: z.ZodIssueCode.custom,
      message: 'Maximum value cannot be less than minimum value',
    });
  }
};

const validateAlphanumericConfig = (
  config: AlphanumericConfig,
  ctx: z.RefinementCtx,
) => {
  const isEnabled = config.value.enabled;
  const isCustomRegex =
    config.value.validationType === AlphanumericValidationType.CUSTOM;

  if (!config.value.validationType && isEnabled) {
    ctx.addIssue({
      path: ['config', 'value', 'validationType'],
      code: z.ZodIssueCode.custom,
      message: 'Validation type is required',
    });
  } else if (isCustomRegex) {
    if (!config.value.regularExpression) {
      ctx.addIssue({
        path: ['config', 'value', 'regularExpression'],
        code: z.ZodIssueCode.custom,
        message: 'Regular expression is required',
      });
    } else {
      try {
        new RegExp(config.value.regularExpression);
      } catch {
        ctx.addIssue({
          path: ['config', 'value', 'regularExpression'],
          code: z.ZodIssueCode.custom,
          message: 'Regular expression is not valid',
        });
      }
    }
  }
};

const validatePhoneNumberConfig = (
  config: PhoneNumberConfig,
  ctx: z.RefinementCtx,
) => {
  if (config.value.enabled && config.value.countryCodes.length === 0) {
    ctx.addIssue({
      path: ['config', 'value', 'countryCodes'],
      code: z.ZodIssueCode.custom,
      message: 'Country codes are required',
    });
  }
};

const validateTimeConfig = (config: TimeConfig, ctx: z.RefinementCtx) => {
  if (config.value.enabled && !config.value.startTime) {
    ctx.addIssue({
      path: ['config', 'value', 'startTime'],
      code: z.ZodIssueCode.custom,
      message: 'Start time is required',
    });
  }
  if (config.value.enabled && !config.value.endTime) {
    ctx.addIssue({
      path: ['config', 'value', 'endTime'],
      code: z.ZodIssueCode.custom,
      message: 'End time is required',
    });
  }

  if (
    config.value.enabled &&
    config.value.startTime &&
    config.value.endTime &&
    config.value.startTime > config.value.endTime
  ) {
    ctx.addIssue({
      path: ['config', 'value', 'endTime'],
      code: z.ZodIssueCode.custom,
      message: 'End time must be after start time',
    });
  }
};
